import { useState, useEffect, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Clock,
  Loader2,
  RefreshCw,
  Send,
  Eye,
  Truck,
  MapPin,
} from "lucide-react";
import { API_URL } from "../config/api";

const COUNTRY_FLAGS = {
  SI: "SI",
  HR: "HR",
  SK: "SK",
  HU: "HU",
  CZ: "CZ",
  AT: "AT",
  IT: "IT",
};

export function RoadClosureSection({ token }) {
  const [scheduleInfo, setScheduleInfo] = useState(null);
  const [countryRules, setCountryRules] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewSubject, setPreviewSubject] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [targetMonth, setTargetMonth] = useState(() => {
    // Default to NEXT month — the newsletter briefs ops BEFORE the
    // month begins, so showing today's month pre-selected led to
    // accidental mid-month sends (e.g. "May 2026" generated on 23 May).
    const now = new Date();
    const m = now.getMonth() + 1; // 1-indexed
    return m === 12 ? 1 : m + 1;
  });
  const [targetYear, setTargetYear] = useState(() => {
    const now = new Date();
    return now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear();
  });
  const [lastResult, setLastResult] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchScheduleInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/road-closure-newsletter/schedule-info`, { headers });
      setScheduleInfo(res.data);
    } catch (err) {
      console.error("Failed to fetch schedule info:", err);
    }
  }, [token]);

  const fetchCountryRules = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/road-closure-newsletter/rules`, { headers });
      setCountryRules(res.data);
    } catch (err) {
      console.error("Failed to fetch country rules:", err);
    }
  }, [token]);

  useEffect(() => {
    fetchScheduleInfo();
    fetchCountryRules();
  }, [fetchScheduleInfo, fetchCountryRules]);

  const handleGenerate = async (force = false) => {
    setGenerating(true);
    setLastResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/road-closure-newsletter/generate`,
        { target_month: targetMonth, target_year: targetYear, force },
        { headers, timeout: 180000 }
      );
      setLastResult(res.data);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.info(res.data.message);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(`Generation failed: ${msg}`);
      setLastResult({ success: false, message: msg });
    } finally {
      setGenerating(false);
    }
  };

  const handlePreview = async () => {
    setGenerating(true);
    setShowPreview(false);
    try {
      const res = await axios.get(
        `${API_URL}/road-closure-newsletter/preview/${targetMonth}/${targetYear}`,
        { headers, timeout: 120000 }
      );
      setPreviewHtml(res.data.html);
      setPreviewSubject(res.data.subject);
      setShowPreview(true);
    } catch (err) {
      toast.error(`Preview failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  return (
    <div className="space-y-6" data-testid="road-closure-section">
      {/* Header */}
      <div className="bg-white rounded-lg border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2" data-testid="road-closure-title">
              <Truck className="h-5 w-5 text-amber-600" />
              Monthly Road Closure Newsletter
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Automated heavy vehicle (&gt;7.5t) traffic restrictions for Central Europe
            </p>
          </div>
        </div>

        {/* Schedule Info */}
        {scheduleInfo && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-testid="schedule-info">
            <div className="flex items-center gap-2 text-blue-800 mb-2">
              <Clock className="h-4 w-4" />
              <span className="font-medium text-sm">Auto-Schedule</span>
            </div>
            <p className="text-sm text-blue-700">
              Next newsletter: <strong>{scheduleInfo.next_newsletter_for}</strong>
            </p>
            <p className="text-xs text-blue-600 mt-1">
              {scheduleInfo.schedule_rule}
            </p>
          </div>
        )}

        {/* Manual Generation */}
        <div className="bg-slate-50 rounded-lg p-4" data-testid="manual-generation">
          <h3 className="font-medium text-sm mb-3 flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Manual Generation
          </h3>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Month</label>
              <select
                value={targetMonth}
                onChange={(e) => setTargetMonth(parseInt(e.target.value))}
                className="border rounded-md px-3 py-2 text-sm bg-white"
                data-testid="month-select"
              >
                {monthNames.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Year</label>
              <select
                value={targetYear}
                onChange={(e) => setTargetYear(parseInt(e.target.value))}
                className="border rounded-md px-3 py-2 text-sm bg-white"
                data-testid="year-select"
              >
                {[2026, 2027, 2028].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <Button
              onClick={() => handleGenerate(false)}
              disabled={generating}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="generate-btn"
            >
              {generating ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> Generate Draft</>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handlePreview}
              disabled={generating}
              data-testid="preview-btn"
            >
              {generating ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading...</>
              ) : (
                <><Eye className="h-4 w-4 mr-2" /> Preview</>
              )}
            </Button>
          </div>

          {/* Generation Result */}
          {lastResult && (
            <div className={`mt-3 p-3 rounded-lg text-sm ${lastResult.success ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-amber-50 border border-amber-200 text-amber-800'}`} data-testid="generation-result">
              <div className="flex items-center gap-2">
                {lastResult.success ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                <span>{lastResult.message}</span>
              </div>
              {lastResult.draft_id && (
                <p className="mt-1 text-xs">
                  Draft ID: {lastResult.draft_id} — Go to <strong>Newsletters</strong> tab to review & approve.
                </p>
              )}
              {lastResult.existing && (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2 text-xs border-amber-400 text-amber-700 hover:bg-amber-100"
                  onClick={() => handleGenerate(true)}
                  disabled={generating}
                  data-testid="regenerate-btn"
                >
                  {generating ? (
                    <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Regenerating...</>
                  ) : (
                    <><RefreshCw className="h-3 w-3 mr-1" /> Regenerate (replace existing)</>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Preview */}
      {showPreview && previewHtml && (
        <Card data-testid="newsletter-preview">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Preview: {previewSubject}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}>Close</Button>
            </div>
          </CardHeader>
          <CardContent>
            <div
              className="border rounded-lg p-4 bg-white overflow-auto max-h-[600px]"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </CardContent>
        </Card>
      )}

      {/* Country Rules Reference */}
      {countryRules && (
        <Card data-testid="country-rules">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="h-4 w-4 text-slate-600" />
              Country Restriction Rules (Reference)
            </CardTitle>
            <p className="text-xs text-slate-500">Last updated: {countryRules.last_updated}</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {Object.entries(countryRules.countries).map(([code, rules]) => (
                <div key={code} className="border rounded-lg p-3 bg-slate-50 hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-sm">{COUNTRY_FLAGS[code]}</span>
                    <span className="font-semibold text-sm">{rules.name}</span>
                  </div>
                  <p className="text-xs text-slate-600 mb-1">Weight: {rules.weight_limit}</p>
                  <div className="space-y-1">
                    {rules.regular_bans?.map((ban, i) => (
                      <div key={i} className="text-xs text-slate-700 flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-slate-400 flex-shrink-0" />
                        <span className="capitalize">{ban.day.replace(/_/g, ' ')}: {ban.time}</span>
                      </div>
                    ))}
                  </div>
                  {rules.recheck_required && (
                    <div className="mt-2 text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Recheck required before dispatch
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
